// Copyright (C) Lembani Sakala - All rights reserved.
// Lembani Sakala.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RootState } from '../../../app/store';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';

import { getUserAsync } from '../../../app/user/userSlice';

import jwtDecode from 'jwt-decode';
import { IDecodedUser } from '../../../interfaces/user';
import { getToken } from '../../../utils/localStorage';

import avatar from '../../../images/user.png';
import { FaEnvelope, FaPhoneAlt, FaTachometerAlt, FaFileInvoice, FaCar, FaUser, FaShieldAlt, FaQuestionCircle } from 'react-icons/fa';
import { UserProfile } from './UserProfile';
import { Overview } from './Overview';
import { QuotationHistory } from './QuotationHistory';
import { UserQuotePreview } from './UserQuotePreview';
import { CarRentalHistory } from './CarRentalHistory';
import { RentalDetails } from './RentalDetails';

const sidebarLinks = [
  { key: 'Dashboard', label: 'Dashboard', icon: <FaTachometerAlt /> },
  { key: 'Quotations', label: 'Car Rental Quotations', icon: <FaFileInvoice /> },
  { key: 'Rentals', label: 'Car Rental Bookings', icon: <FaCar /> },
  { key: 'Profile', label: 'My Profile', icon: <FaUser /> },
  { key: 'Password & Security', label: 'Password & Security', icon: <FaShieldAlt /> },
];

export const Dashboard = () => {
  const [activeLink, setActiveLink] = useState('Dashboard');
  const dispatch = useAppDispatch();
  const history = useNavigate();
  const { section } = useParams();

  const { userInfo } = useAppSelector((state: RootState) => state.user);

  const tk = getToken();
  let d: IDecodedUser;
  let uid = 0;
  if (tk !== null) {
    d = jwtDecode(tk);
    uid = d.sub;
  }

  const handleLinkClick = (link: string) => {
    setActiveLink(link);
    const quotationId = link.startsWith('quotations/') ? link.split('/')[1] : null;
    if (quotationId !== null) {
      history(`/dashboard/${link}`);
    } else {
      history(`/dashboard/${link.toLowerCase().replace(/\s/g, '-')}`);
    }
  };

  useEffect(() => {
    dispatch(getUserAsync(uid));
  }, [dispatch, uid]);

  return (
    <div className='dash-page'>
      <div className='dash-layout'>
        {/* Sidebar */}
        <aside className='dash-sidebar'>
          <div className='dash-sidebar-profile'>
            <div className='dash-avatar-wrapper'>
              <img src={avatar} className='dash-avatar' alt={userInfo?.firstname} />
            </div>
            <h5 className='dash-profile-name'>
              {userInfo?.firstname} {userInfo?.lastname}
            </h5>
            <div className='dash-profile-contact'>
              <span><FaEnvelope /> {userInfo?.username}</span>
              <span><FaPhoneAlt /> {userInfo?.phone}</span>
            </div>
          </div>

          <nav className='dash-nav'>
            {sidebarLinks.map((link) => (
              <div
                key={link.key}
                onClick={() => handleLinkClick(link.key)}
                className={`dash-nav-item ${activeLink === link.key ? 'dash-nav-active' : ''}`}
              >
                <span className='dash-nav-icon'>{link.icon}</span>
                <span>{link.label}</span>
              </div>
            ))}
            <div className='dash-nav-divider' />
            <div
              onClick={() => handleLinkClick('Help')}
              className={`dash-nav-item ${activeLink === 'Help' ? 'dash-nav-active' : ''}`}
            >
              <span className='dash-nav-icon'><FaQuestionCircle /></span>
              <span>Help & Support</span>
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <main className='dash-main'>
          {section === 'dashboard' && <Overview />}
          {section === 'profile' && <UserProfile />}
          {section?.startsWith('quotations/') && <UserQuotePreview />}
          {section === 'quotations' && <QuotationHistory />}
          {section?.startsWith('rentals/') && <RentalDetails />}
          {section === 'rentals' && <CarRentalHistory />}
        </main>
      </div>
    </div>
  );
};
